/* eslint-disable camelcase */
import { Box, Button, Grid, TextField, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { CountryData } from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import { useAppDispatch, useAppSelector } from '../../../_helpers/hooks'
import { IContactUs } from '../../../_models/commonInterface'
import PhoneNumberInput from '../../../components/phoneInput'
import BgCircleImage from '../../../images/bg-shape.svg'
import ContactImage from '../../../images/contactUs.svg'
import BgShapesImage from '../../../images/shapes.svg'
import { contactUs, publicPagesState } from '../../../slices/publicPagesSlice'
const ContactUs = () => {
  type FormInputs = {
    name: string
    organization?: string
    email: string
    phone?: string
    message: string
  }
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const {
    handleSubmit,
    control,
    formState: { isSubmitted },
    reset,
    setValue,
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      organization: '',
      message: '',
    },
  })
  const [countryData, setCountryData] = useState<CountryData | Record<string, never>>()
  const { i18n, t } = useTranslation()
  const { isContactFormSubmitted } = useAppSelector(publicPagesState)
  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    let modifiedFormData: IContactUs
    if (countryData?.dialCode !== undefined && data.phone !== '' && data.phone !== undefined) {
      modifiedFormData = {
        ...data,
        phone: parseInt(data.phone?.replace(countryData.dialCode, ''), 10),
        country_code: parseInt(countryData.dialCode, 10),
      }
    } else {
      modifiedFormData = {
        ...data,
        phone: null,
        country_code: null,
      }
    }
    dispatch(contactUs(modifiedFormData))
  }
  useEffect(() => {
    if (isContactFormSubmitted) {
      reset()
      setValue('phone', '1')
    }
  }, [isContactFormSubmitted, reset, setValue])
  const handleChange = (data: CountryData | Record<string, never>) => {
    setCountryData(data)
  }

  return (
    <Grid
      container
      spacing={1}
      sx={{
        [theme.breakpoints.down(550)]: {
          zoom: '70%',
        },
        [theme.breakpoints.down(400)]: {
          zoom: '60%',
        },
      }}
      mr={4}
      mb={{ xs: 20, md: 10 }}
    >
      <Grid item xs={12} sm={12} md={5} mt={17}>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          ml={{
            xs: i18n.language === 'ja' ? 8 : 5,
            sm: i18n.language === 'ja' ? 12 : 7,
            lg: i18n.language === 'ja' ? 14 : 0,
          }}
        >
          <Box>
            <Typography
              variant='h1'
              fontSize={'3.25rem'}
              color={'publicPages.light'}
              lineHeight={'3.638rem'}
              fontWeight={'bold'}
            >
              <Trans
                i18nKey='contactUs.contactUsText'
                components={{
                  highlight: <span style={{ color: theme.palette.primary.main }} />,
                  br: <br />,
                }}
              />
            </Typography>
            <Typography
              variant='subtitle1'
              fontSize={'1.25rem'}
              color={'publicPages.main'}
              lineHeight={'1.875rem'}
              fontWeight={300}
              sx={{ my: 5 }}
            >
              <Trans
                i18nKey='contactUs.contactUsText1'
                components={{
                  highlight: (
                    <span style={{ color: theme.palette.primary.main, fontWeight: 500 }} />
                  ),
                  br: <br />,
                }}
              />
            </Typography>
            <Box mt={6}>
              <Box component='img' src={ContactImage} alt={t('contactUs.contactUsImage')} />
            </Box>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12} md={7} mt={5} mb={4}>
        <Box
          sx={{
            position: 'relative',
            width: '95%',
            height: '90%',
          }}
        >
          <Box
            component='img'
            src={BgCircleImage}
            alt={t('contactUs.contactUsImage')}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
            }}
          />
          <Box
            component='img'
            src={BgShapesImage}
            alt={'contactUs.backgroundImage'}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              top: '78%',
            }}
          />
          <Box
            ml={{ xs: '15%', sm: 15, md: 23 }}
            mt={8}
            sx={{
              borderRadius: '1rem',
              boxShadow: '0 0.125rem 1.5rem 0 rgba(0, 0, 0, 0.1)',
              backgroundColor: 'common.white',
              maxWidth: { xs: '100%', lg: '75%' },
              padding: '2.25rem',
              position: 'absolute',
              [theme.breakpoints.down(1035)]: {
                position: 'relative',
              },
            }}
          >
            <Typography
              variant='h1'
              fontSize={'1.625rem'}
              color={'iconColors.main'}
              lineHeight={1.12}
              fontWeight={'bold'}
            >
              {t('contactUs.contactUsBtn')}
            </Typography>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mt: 1 }}>
                <Controller
                  name='name'
                  control={control}
                  defaultValue=''
                  rules={{
                    required: t('contactUs.nameIsRequired'),
                    minLength: {
                      value: 1,
                      message: t('contactUs.nameLengthRangeError'),
                    },
                    maxLength: {
                      value: 50,
                      message: t('contactUs.nameLengthRangeError'),
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      margin='normal'
                      fullWidth
                      label={`${t('contactUs.namePlaceholder')} *`}
                      error={isSubmitted && !!error}
                      helperText={isSubmitted && error ? error.message : null}
                      sx={{ mt: 2 }}
                      aria-label={t('contactUs.namePlaceholder')}
                    />
                  )}
                />
                <Controller
                  name='email'
                  control={control}
                  defaultValue=''
                  rules={{
                    required: t('contactUs.emailAddressRequired'),
                    maxLength: {
                      value: 64,
                      message: t('contactUs.emailMaxLengthError'),
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9._+]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/,

                      message: t('contactUs.emailValid'),
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      margin={'dense'}
                      fullWidth
                      label={`${t('contactUs.emailPlaceholder')} *`}
                      error={isSubmitted && !!error}
                      helperText={isSubmitted && error ? error.message : null}
                      sx={{ mt: 2 }}
                      aria-label={t('contactUs.emailPlaceholder')}
                    />
                  )}
                />

                <Box sx={{ mt: 1.5 }}>
                  <Controller
                    name='phone'
                    control={control}
                    defaultValue=''
                    rules={{
                      pattern: {
                        value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4,9}$/,
                        message: `${t('contactUs.phoneErrorMsg')}`,
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Box
                        component='div'
                        className={error && isSubmitted ? 'error' : ''}
                        sx={{ mt: 3, mb: 1 }}
                      >
                        <PhoneNumberInput
                          style={{ width: '100%' }}
                          phoneField={{
                            name: field.name,
                            value: field.value,
                            onBlur: field.onBlur,
                            isValid: !(isSubmitted && error?.message),
                            onChange: (value, data) => {
                              field.onChange(value)
                              handleChange(data)
                            },
                          }}
                          isMandatory={false}
                        />
                        {isSubmitted && error?.message ? (
                          <Typography variant='caption' color='error.main' sx={{ ml: 1.5 }}>
                            {error.message}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  />
                </Box>
                <Controller
                  name='organization'
                  control={control}
                  defaultValue=''
                  rules={{
                    minLength: {
                      value: 1,
                      message: t('contactUs.organisationNameCharLimit'),
                    },
                    maxLength: {
                      value: 255,
                      message: t('contactUs.organisationNameCharLimit'),
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      margin={'normal'}
                      fullWidth
                      label={t('contactUs.organizationText')}
                      error={isSubmitted && !!error}
                      helperText={isSubmitted && error ? error.message : null}
                      sx={{ mt: 2 }}
                      aria-label={t('contactUs.organizationText')}
                    />
                  )}
                />
                <Controller
                  name='message'
                  control={control}
                  defaultValue=''
                  rules={{
                    required: t('contactUs.messageRequired'),
                    minLength: {
                      value: 1,
                      message: t('contactUs.messageCharLimit'),
                    },
                    maxLength: {
                      value: 800,
                      message: t('contactUs.messageCharLimit'),
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      multiline
                      rows={5}
                      {...field}
                      margin={'normal'}
                      fullWidth
                      label={t('contactUs.messageText')}
                      error={isSubmitted && !!error}
                      helperText={isSubmitted && error ? error.message : null}
                      sx={{ mt: 2 }}
                      aria-label={t('contactUs.messageText')}
                    />
                  )}
                />

                <Button
                  type='submit'
                  variant='contained'
                  sx={{ mt: 3, mb: 1, height: 60, width: 200 }}
                  size='large'
                  aria-label={t('contactUs.sendMessageButton')}
                >
                  <Typography variant='h5'> {t('contactUs.sendMessageButton')}</Typography>
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default ContactUs
